import { Link } from "react-router-dom";
import { Mail as MailIcon, MessagesSquare, Phone } from "lucide-react";
import UserAvatar from "@/components/common/UserAvatar";

type AnyRecord = Record<string, any>;

/**
 * The point-of-contact card, shared by every portal. The client portal never
 * showed a phone number (`ClientDealEngagement.tsx`'s original `ContactCard`
 * deliberately omitted it); collaborators get one when the agent card carries
 * one, since a lender or attorney coordinating a closing has real reason to
 * call rather than email.
 */
interface PortalContactCardProps {
  agent: AnyRecord | null | undefined;
  roleLabel: string;
  testId?: string;
  /** Where "Send a message" goes — the Chats page on this deal. The live
   *  line is offered ahead of email. */
  messageTo?: string;
}

export default function PortalContactCard({ agent, roleLabel, testId = "portal-contact-card", messageTo }: PortalContactCardProps) {
  if (!agent?.name) return null;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150 ease-out" data-testid={testId}>
      <div className="text-[12px] font-bold uppercase tracking-[1.2px] text-dash-muted mb-2">Point of contact</div>
      <div className="bg-white border border-dash-border rounded-2xl p-6 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <UserAvatar name={agent.name} src={agent.photo_url} className="size-12" fallbackClassName="bg-dash-greenMid text-base font-bold text-white" />
          <div className="min-w-0">
            <div className="text-lg font-semibold text-dash-ink truncate">{agent.name}</div>
            <div className="text-[13px] text-dash-muted">{roleLabel}</div>
          </div>
        </div>
        {messageTo && (
          <Link
            to={messageTo}
            className="flex items-center gap-2.5 rounded-full bg-primary px-4 py-3 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover"
            data-testid="portal-contact-message"
          >
            <MessagesSquare className="w-4 h-4 flex-shrink-0" />
            Send a message
          </Link>
        )}
        {agent.email && (
          <a
            href={`mailto:${agent.email}`}
            className="flex items-center gap-2.5 bg-dash-page border border-dash-border rounded-full px-4 py-3 text-[13px] font-medium text-dash-muted hover:bg-bone transition-colors"
            data-testid="portal-contact-email"
          >
            <MailIcon className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{agent.email}</span>
          </a>
        )}
        {agent.phone && (
          <a
            href={`tel:${agent.phone}`}
            className="flex items-center gap-2.5 bg-dash-page border border-dash-border rounded-full px-4 py-3 text-[13px] font-medium text-dash-muted hover:bg-bone transition-colors"
            data-testid="portal-contact-phone"
          >
            <Phone className="w-4 h-4 flex-shrink-0" />
            {agent.phone}
          </a>
        )}
      </div>
    </div>
  );
}
